import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INGENIUM_LOGO = "https://customer-assets.emergentagent.com/job_agentic-curation/artifacts/zri9wsj7_ingenium-logo.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset link.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success("Password reset successfully!");
      
      // Redirect to home page after 3 seconds
      setTimeout(() => {
        navigate("/?login=true");
      }, 3000);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to reset password. The link may have expired.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#191b1d] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/">
            <img 
              src={INGENIUM_LOGO} 
              alt="Ingenium Digital" 
              className="h-10 mx-auto mb-6"
            />
          </a>
        </div>

        <Card className="feature-card">
          <CardContent className="p-8">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2 font-mono">
                  Password Reset!
                </h1>
                <p className="text-[#ebebeb]/60 font-mono text-sm">
                  Your password has been reset successfully.<br />
                  Redirecting you to login...
                </p>
              </div>
            ) : error && !token ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2 font-mono">
                  Invalid Link
                </h1>
                <p className="text-[#ebebeb]/60 font-mono text-sm mb-6">
                  {error}
                </p>
                <a 
                  href="/?login=true"
                  className="ingenium-button inline-block"
                >
                  <span>Back to Login</span>
                </a>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#ff3737]/20 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#ff3737]" />
                  </div>
                  <h1 className="text-2xl font-bold text-white mb-2 font-mono">
                    Reset Your Password
                  </h1>
                  <p className="text-[#ebebeb]/60 font-mono text-sm">
                    Enter your new password below
                  </p>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                    <p className="text-red-400 text-sm font-mono">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#ebebeb]">New Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="form-input"
                      data-testid="new-password-input"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#ebebeb]">Confirm Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="form-input"
                      data-testid="confirm-password-input"
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="ingenium-button w-full text-center"
                    data-testid="reset-password-btn"
                  >
                    <span className="flex items-center justify-center gap-2">
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4" />
                          Reset Password
                        </>
                      )}
                    </span>
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <a 
                    href="/?login=true" 
                    className="text-[#ebebeb]/60 hover:text-white text-sm font-mono"
                  >
                    Back to Login
                  </a>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
